import React, { useState, useRef } from "react";
import { useSiteContent } from "../../services/content";
import { checkSubmission, throttled, markSubmitted } from "../../utils/spamGuard";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Chip from "@mui/material/Chip";
import OutlinedInput from "@mui/material/OutlinedInput";
import { motion } from "framer-motion";
import { fig } from "../../theme";

const interestOptions = [
    "FIG Finance",
    "Hi5",
    "FIG KIDS (CACFP)",
    "FIG K12 LMS",
    "Bid-FIG / Procurement",
    "Invoice Audit",
    "AIOS Platform",
    "CRM",
    "Swan AI Labs",
    "Custom Development",
    "Other",
];

const initialState = {
    firstName: "",
    lastName: "",
    organization: "",
    email: "",
    phone: "",
    role: "",
    interest: [],
    details: "",
    // Honeypot: hidden from real users, irresistible to form-filling bots.
    website: "",
};

export default function Contact() {
    const content = useSiteContent();
    const [form, setForm] = useState(initialState);
    const [status, setStatus] = useState({ success: null, error: null, loading: false });
    const mountedAt = useRef(Date.now());

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({
            ...form,
            [name]: name === "interest" ? (typeof value === "string" ? value.split(",") : value) : value,
        });
    };

    /** Returns an error message to show the visitor, or null when the form is good. */
    const validate = () => {
        if (
            !form.firstName.trim() ||
            !form.lastName.trim() ||
            !form.organization.trim() ||
            !form.email.trim() ||
            !form.phone.trim() ||
            !form.details.trim()
        ) return "Please fill out all required fields.";

        const reason = checkSubmission({
            name: `${form.firstName} ${form.lastName}`,
            email: form.email,
            phone: form.phone,
            message: form.details,
            honeypot: form.website,
            elapsedMs: Date.now() - mountedAt.current,
        });
        if (!reason) return null;

        // Map the internal reason to something a real person can act on.
        const messages = {
            "invalid email address": "Please enter a valid email address.",
            "phone number must be 10 digits": "Please enter a 10-digit US phone number.",
            "not a valid US phone number": "Please enter a valid US phone number.",
            "please add a little more detail": "Please tell us a bit more about what you need.",
        };
        return messages[reason] || "We couldn't accept this submission. If you're a real person, email us directly at dusty@swank12.com.";
    };

    // Visitor input lands in an HTML email we read — escape it so a hostile
    // submission cannot inject markup or links into the notification.
    const esc = (v) => String(v ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    const buildHtml = () => `
    <div style="font-family:Segoe UI,Arial,sans-serif;max-width:600px;margin:0 auto;">
      <h2 style="background:#1ebec8;padding:18px 0;color:#ffffff;text-align:center;margin-top:0;">
        New Swan Solutions Contact Request
      </h2>
      <table style="width:100%;font-size:1rem;border-collapse:collapse;">
        <tr><td style="font-weight:bold;padding:8px;">First Name:</td><td>${esc(form.firstName)}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Last Name:</td><td>${esc(form.lastName)}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">District/Organization:</td><td>${esc(form.organization)}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Email:</td><td>${esc(form.email)}</td></tr>
        <tr><td style="font-weight:bold;padding:8px;">Phone:</td><td>${esc(form.phone)}</td></tr>
        ${form.role ? `<tr><td style="font-weight:bold;padding:8px;">Role/Title:</td><td>${esc(form.role)}</td></tr>` : ""}
        ${form.interest.length ? `<tr><td style="font-weight:bold;padding:8px;">Interest:</td><td>${esc(form.interest.join(", "))}</td></tr>` : ""}
        <tr><td style="font-weight:bold;padding:8px;">Details/Message:</td><td>${esc(form.details).replace(/\n/g, "<br/>")}</td></tr>
      </table>
      <hr style="margin:30px 0 12px 0;">
      <div style="color:#5f6b73;font-size:0.96rem;">Contact form submitted from <b>swansolutions.com</b></div>
    </div>
  `;

    const buildText = () => `
New Swan Solutions Contact Request

First Name: ${form.firstName}
Last Name: ${form.lastName}
District/Organization: ${form.organization}
Email: ${form.email}
Phone: ${form.phone}
${form.role ? `Role/Title: ${form.role}` : ""}
${form.interest.length ? `Interest: ${form.interest.join(", ")}` : ""}
Details/Message:
${form.details}
  `;

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Honeypot filled = bot. Show the success screen so it does not retry
        // with a different payload, but send nothing.
        if (form.website) {
            setStatus({ loading: false, success: "Thank you! Your message has been sent.", error: null });
            setForm(initialState);
            return;
        }

        if (throttled()) {
            setStatus({ ...status, error: "You just sent a message. Please wait a moment before sending another." });
            return;
        }

        const invalid = validate();
        if (invalid) {
            setStatus({ ...status, error: invalid });
            return;
        }
        setStatus({ loading: true, error: null, success: null });
        try {
            const res = await fetch("https://mailgun.swancreate.com/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    to: "dusty@swank12.com,becki@figk-12.com",
                    subject: `New Contact Request - ${form.organization} (${form.firstName} ${form.lastName})`,
                    replyTo: form.email,
                    html: buildHtml(),
                    text: buildText(),
                }),
            });
            if (res.ok) {
                try {
                    await fetch("https://figazuresql.swancreate.com/api/crm/sk12_contactsubmissions", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            Name: `${form.firstName} ${form.lastName}`,
                            Email: form.email,
                            Phone: form.phone,
                            Interests: form.interest.length ? JSON.stringify(form.interest) : "",
                            Message: form.details,
                            Status: "New",
                        }),
                    });
                } catch (e) { /* non-fatal: email already sent */ }
                markSubmitted();
                setStatus({ loading: false, success: "Thank you! Your message has been sent.", error: null });
                setForm(initialState);
            } else {
                throw new Error();
            }
        } catch {
            setStatus({ loading: false, error: "Sorry, there was a problem sending your message. Please try again.", success: null });
        }
    };

    return (
        <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
            {/* Hero */}
            <Box
                component={motion.section}
                initial={{ opacity: 0, y: -64 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                sx={{
                    width: "100vw",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    px: 0,
                    py: { xs: 10, md: 14 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background: fig.gradientHero,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    minHeight: { xs: "30vh", md: "36vh" },
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 6px 40px 0 rgba(30,190,200,0.18)",
                    mb: 8,
                }}
            >
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: "2.1rem", md: "2.8rem" },
                        color: "#fff",
                        mb: 2,
                        letterSpacing: "-0.04em",
                        textShadow: "0 3px 16px rgba(15,90,100,0.18)",
                    }}
                >
                    {content("contact.hero.title", "Contact Swan Solutions")}
                </Typography>
                <Typography
                    variant="h5"
                    sx={{
                        color: "rgba(255,255,255,0.95)",
                        fontWeight: 400,
                        maxWidth: 650,
                        mx: "auto",
                        fontSize: { xs: "1.05rem", md: "1.18rem" },
                        mb: 1,
                        textShadow: "0 2px 14px rgba(15,140,150,0.16)",
                    }}
                >
                    {content("contact.hero.subtitle", "Let us know about your project, interest, or request, and a real person will follow up within one business day.")}
                </Typography>
            </Box>

            <Container maxWidth="sm" sx={{ bgcolor: "background.paper", py: 6, px: { xs: 3, sm: 5 }, borderRadius: 4, boxShadow: "0 18px 50px -20px rgba(30,190,200,0.25)", border: "1px solid rgba(30,190,200,0.12)", mb: 12 }}>
                <form onSubmit={handleSubmit} autoComplete="off">
                    {/*
                      Honeypot. Positioned off-screen rather than display:none —
                      the better bots skip display:none fields. Real users never
                      see it or tab to it; automated fillers populate it.
                    */}
                    <Box
                        aria-hidden="true"
                        sx={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}
                    >
                        <label htmlFor="sk12-website">Website (leave blank)</label>
                        <input
                            id="sk12-website"
                            type="text"
                            name="website"
                            value={form.website}
                            onChange={handleChange}
                            tabIndex={-1}
                            autoComplete="off"
                        />
                    </Box>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                label="First Name"
                                name="firstName"
                                value={form.firstName}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                label="Last Name"
                                name="lastName"
                                value={form.lastName}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                label="District or Organization Name"
                                name="organization"
                                value={form.organization}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                label="Email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                                type="email"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                label="Phone"
                                name="phone"
                                value={form.phone}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                                type="tel"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Your Role/Title"
                                name="role"
                                value={form.role}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel id="interest-label">Interested In</InputLabel>
                                <Select
                                    labelId="interest-label"
                                    multiple
                                    name="interest"
                                    value={form.interest}
                                    onChange={handleChange}
                                    input={<OutlinedInput label="Interested In" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip key={value} label={value} sx={{ bgcolor: "#eefbfb", color: "text.primary" }} />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {interestOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                label="Details or What Services You Need"
                                name="details"
                                value={form.details}
                                onChange={handleChange}
                                fullWidth
                                variant="outlined"
                                multiline
                                minRows={5}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 2 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    component={motion.button}
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.96 }}
                                    transition={{ type: "spring", stiffness: 330, damping: 16 }}
                                    sx={{
                                        borderRadius: 99,
                                        px: 5,
                                        py: 1.3,
                                        fontWeight: 700,
                                        fontSize: "1.15rem",
                                        color: "#fff",
                                        background: fig.gradient,
                                        boxShadow: "0 10px 26px -8px rgba(30,190,200,0.5)",
                                    }}
                                    disabled={status.loading}
                                >
                                    {status.loading ? "Sending..." : "Send Message"}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
                <Box sx={{ mt: 3 }}>
                    {status.success && <Alert severity="success">{status.success}</Alert>}
                    {status.error && <Alert severity="error">{status.error}</Alert>}
                </Box>
            </Container>
        </Box>
    );
}
